import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import html2canvas from 'html2canvas';
import { cloneDeep, isEmpty } from 'lodash';
import * as mapboxgl from 'mapbox-gl';
import { forkJoin, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, skipWhile, take, takeUntil, timeout } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { CohortService } from '../../../core/api/cohort.service';
import { DocumentService } from '../../../core/api/document.service';
import { GisService } from '../../../core/api/gis.service';
import { SiteService } from '../../../core/api/site.service';
import { EditMapDeactivateModalService } from '../../../core/auth/edit-map-deactivate-modal.service';
import { DeactivationGuarded } from '../../../core/auth/edit-map.guard';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { RedirectService } from '../../../core/helper/redirect.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { DialogChangeActingAsUserComponent } from '../../../shared/dialogs/dialog-change-acting-as-user/dialog-change-acting-as-user.component';
import { DialogOverlappingStandsComponent } from '../../../shared/dialogs/dialog-overlapping-stands/dialog-overlapping-stands.component';
import { SiteSizeDialogComponent } from '../../../shared/dialogs/dialog-site-size/dialog-site-size.component';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Buyer, Cohort, Parcel, Region, Site, User } from '../../../shared/models';
import { ToastLevel } from '../../../shared/models/toast-message.module';
import { ToastService } from '../../../shared/services/toast.service';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { setActAsUser } from '../../../store/auth/auth.actions';
import { selectActAsUser, selectCurrentUser } from '../../../store/auth/auth.selectors';
import { createDocument } from '../../../store/document/document.actions';
import {
  toggleMapControlsForest,
  toggleMapControlsKMLUpload,
  toggleMapControlsPublicLand,
  toggleMapControlsStands,
  toggleMapControlsWaterways,
} from '../../../store/map-control/map-control.actions';
import { MapControlState, MapDisplayTypeEnum } from '../../../store/map-control/map-control.reducer';
import { selectMapControlState } from '../../../store/map-control/map-control.selectors';
import {
  removeRedFlagFailure,
  removeRedFlagSuccess,
  resetParcels,
  selectAllStands,
} from '../../../store/parcel/parcel.actions';
import { selectForestryKml, selectSelectedStands } from '../../../store/parcel/parcel.selectors';
import {
  resetReadyForSubmit,
  setActiveSite,
  setLastSaveIsValid,
  updateNewImprovedSiteSuccess,
  updateSiteFailure,
  updateSiteStep,
} from '../../../store/site/site.actions';
import { selectActiveSite, selectReadOnlyMapInfo, selectReadyForSubmit } from '../../../store/site/site.selectors';
import { MapLayersComponent } from '../map-layers/map-layers.component';

export interface MapEvent {
  action: string;
  event: any;
}

@Component({
  selector: 'fc-map-layers-page',
  templateUrl: './map-layers-page.component.html',
  styleUrls: ['./map-layers-page.component.scss'],
})
export class MapLayersPageComponent implements OnInit, OnDestroy, AfterViewInit, DeactivationGuarded {
  @ViewChild('drawer') drawer: MatDrawer;
  @ViewChild(MapLayersComponent, { static: false })
  mapLayersComponent: MapLayersComponent;

  destroy$: Subject<boolean> = new Subject<boolean>();
  mapEvents$: Subject<MapEvent> = new Subject<MapEvent>();
  standListHover$: Subject<{ isHovered: boolean; standId: string }> = new Subject<{
    isHovered: boolean;
    standId: string;
  }>();

  submitDialogRef: MatDialogRef<DialogYesNoComponent> = null;
  overlappingDialogRef: MatDialogRef<DialogOverlappingStandsComponent> = null;

  siteId: number;
  selectedStandList: any = [];
  activeSite: Site = null;
  acreage: string;
  map: mapboxgl.Map = null;
  originalBoundingBox: any = null;
  readOnlyMap: boolean;
  canSaveMap: boolean = false;
  absMin: number = environment.site.minSize;
  currentUser: User;
  actAsUser: User;
  isAdmin: boolean;
  isLimitedAdmin: boolean = false;
  contactPhone = '';
  contactEmail = '';
  cohort: Cohort = null;
  region: Region = null;
  preferredBuyer: Buyer = null;
  openCohorts: Cohort[] = null;
  valuationPerYear: string;
  potentialRevenue: string = 'Calculating...';

  // Map Input Data
  mapParcels: Array<Parcel>;
  mapForestryKml: Array<any>;

  twentyYearMark: string = null;
  twentyYearProjection = 'N/A';
  currentMapControls: MapControlState;
  screenshotDefinition;
  publicOverlap: number = 0;
  isSelectAllDisabled: boolean = false;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly gisService: GisService,
    readonly formatHelper: FormatHelperService,
    readonly cohortService: CohortService,
    readonly toastService: ToastService,
    readonly documentService: DocumentService,
    readonly redirectService: RedirectService,
    readonly editMapDeactivateModalService: EditMapDeactivateModalService,
    readonly spinnerService: SpinnerService,
    readonly http: HttpClient,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.siteId = +this.route.snapshot.params.id;
  }

  ngOnInit() {
    this.store
      .select(selectForestryKml)
      .pipe(takeUntil(this.destroy$))
      .subscribe(forestryKml => {
        this.mapForestryKml = forestryKml;
      });

    forkJoin([
      this.store.select(selectAppConfigState).pipe(
        timeout(10000),
        skipWhile(appConfig => appConfig.config.length === 0),
        take(1)
      ),
      this.store.select(selectCurrentUser).pipe(
        timeout(10000),
        skipWhile(currentUser => currentUser === null),
        take(1)
      ),
      this.store.select(selectActAsUser).pipe(take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([appConfig, currentUser, actAsUser]) => {
        if (!isEmpty(appConfig.config.abs_min_size)) {
          this.absMin = parseInt(appConfig.config.abs_min_size, 10);
        }
        this.contactEmail = appConfig.config.fc_help_email;
        this.contactPhone = appConfig.config.fc_help_phone;
        this.currentUser = currentUser;
        this.actAsUser = actAsUser;
        this.preferredBuyer = this.currentUser?.preferredBuyer;
        this.isAdmin = this.currentUser ? this.currentUser.isAdmin : false;
        this.isLimitedAdmin = this.currentUser?.hasRole(RoleEnum.LimitedAdmin);
      });

    this.http
      .get(
        `https://api.mapbox.com/styles/v1/${environment.mapbox.contourStyleId}?access_token=${environment.mapbox.accessToken}`
      )
      .pipe(take(1))
      .subscribe((result: any) => {
        // set the topographic line color and thickness
        const contourLineIdx = result.layers.findIndex(l => l.id === 'contour-line');
        if (contourLineIdx > -1) {
          result.layers.splice(contourLineIdx, 1);
        }
        this.screenshotDefinition = result;
      });

    this.store
      .select(selectReadyForSubmit)
      .pipe(
        skipWhile(readyForSubmit => !readyForSubmit),
        takeUntil(this.destroy$)
      )
      .subscribe(readyForSubmit => {
        if (readyForSubmit && this.openCohorts.length > 0) {
          this.store.dispatch(resetReadyForSubmit());
          if (this.isAdmin) {
            this.siteHelper.askForApprovalEmail(
              this.activeSite.id,
              'Do you wish to send an email requesting approval?',
              'Site Details',
              `/admin/site/${this.activeSite.id}`
            );
          } else {
            this.submitDialogRef = this.dialog.open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Map Saved',
                line1:
                  'Your carbon offset site has been saved. If you have completed your site, you may continue the submission process now, or you may return to CORE Carbon in the future and pick up where you left off.',
                buttonTextYes: 'Continue Submission',
                buttonTextNo: 'Return to Map',
              },
              autoFocus: false,
            });

            this.submitDialogRef.afterClosed().subscribe(result => {
              if (result) {
                if (
                  this.actAsUser &&
                  this.actAsUser.id === this.currentUser.id &&
                  this.currentUser.landowners.length > 0
                ) {
                  //Show change user dialog?
                  this.dialog
                    .open(DialogChangeActingAsUserComponent, {
                      data: {
                        currentUser: this.currentUser,
                      },
                    })
                    .afterClosed()
                    .subscribe(result => {
                      if (result) {
                        this.dialog
                          .open(DialogYesNoComponent, {
                            panelClass: 'fc-confirm-change-acting-as-user',
                            data: {
                              title: 'Confirm',
                              line1:
                                'This will change ownership of this pending site to ' +
                                result.getDisplayName() +
                                '. You will not be able to change it back to yourself.',
                              line2:
                                'Please confirm if you wish to proceed acting as yourself, or if you want to change ownership to ' +
                                result.getDisplayName() +
                                '.',
                              buttonTextYes: 'Change Owner',
                              buttonTextNo: 'Proceed as Me',
                            },
                            autoFocus: false,
                          })
                          .afterClosed()
                          .subscribe(proceed => {
                            if (proceed) {
                              // change account for current site
                              // change acting as user
                              this.siteService.updateSiteOwner(this.activeSite.id, result.id).subscribe(updatedSite => {
                                this.store.dispatch(setActAsUser({ data: result }));
                                this.store.dispatch(
                                  updateNewImprovedSiteSuccess({
                                    site: updatedSite,
                                    readyForSubmit: false,
                                  })
                                );
                              });
                            }
                            this.doSubmitSite();
                          });
                      } else {
                        this.doSubmitSite();
                      }
                    });
                } else {
                  this.doSubmitSite();
                }
              }
              this.submitDialogRef = null;
            });
          }
        }
      });

    this.store
      .select(selectActiveSite)
      .pipe(
        filter(site => !isEmpty(site)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroy$)
      )
      .subscribe(activeSite => {
        if (activeSite) {
          this.activeSite = activeSite;
          this.isSelectAllDisabled =
            this.activeSite.status === StatusEnum.REQUIRES_LANDOWNER_APPROVAL && !(this.isAdmin || this.isLimitedAdmin);
          if (this.isAdmin && this.activeSite.submittedDate !== null && this.activeSite.activationDate === null) {
            this.siteService.sitePublicOverlap(this.activeSite.id).subscribe(overlapAcres => {
              if (overlapAcres) {
                this.publicOverlap = overlapAcres;
              }
            });
          }
          this.acreage =
            this.acreage ||
            this.activeSite.area.toLocaleString('us-EN', {
              maximumFractionDigits: 2,
            });
          this.mapParcels = this.activeSite.versions.slice(-1)[0].parcels;
          this.region = this.activeSite.cohort.region;
          this.cohort = this.activeSite.cohort;
          if (this.activeSite.submittedDate !== null) {
            this.openCohorts = [this.cohort];
          } else {
            this.openCohorts = this.activeSite.availableCohorts;
          }
          const parcels = cloneDeep(this.mapParcels);
          const stands = cloneDeep(this.activeSite.versions.slice(-1)[0].stands);
          this.activeSite.simulatedFinancials.stand_financials.forEach(stand_financial => {
            stands.forEach(stand => {
              if (stand.customId == stand_financial.custom_id) {
                stand.pricingData = stand_financial;
                stand.priceValuation = this.formatHelper.formatMinMaxValuation(
                  stand_financial.annual_valuation.map(obj => obj.valuation)
                );
              }
            });
          });
          this.store.dispatch(
            resetParcels({
              data: {
                parcels,
                stands,
                unsavedChanges: false,
              },
            })
          );
        }
      });

    if (!this.activeSite) {
      this.store.dispatch(setActiveSite({ id: this.siteId }));
    }

    this.store
      .select(selectSelectedStands)
      .pipe(
        skipWhile(() => this.activeSite === null),
        takeUntil(this.destroy$)
      )
      .subscribe(stands => {
        this.selectedStandList = stands;
        let annual_valuation = [0, 0];
        let twenty_valuation = [0, 0];
        let acreage = 0;
        if (stands && this.activeSite !== null) {
          //calculate total ERT and area
          for (const s of stands) {
            acreage += s.area;
            annual_valuation = [
              Math.min(...annual_valuation) + Math.min(...s.pricingData.annual_valuation.map(obj => obj.valuation)),
              Math.max(...annual_valuation) + Math.max(...s.pricingData.annual_valuation.map(obj => obj.valuation)),
            ];
            twenty_valuation = [
              Math.min(...twenty_valuation) + Math.min(...s.pricingData.twenty_valuation.map(obj => obj.valuation)),
              Math.max(...twenty_valuation) + Math.max(...s.pricingData.twenty_valuation.map(obj => obj.valuation)),
            ];
          }
          if (this.region) {
            this.twentyYearMark = this.activeSite.getYearMarkString(0);
            this.twentyYearProjection = this.formatHelper.formatMinMaxValuation(twenty_valuation);
          }
          this.valuationPerYear = this.formatHelper.formatMinMaxValuation(annual_valuation);
          this.acreage = acreage.toLocaleString('us-EN', {
            maximumFractionDigits: 2,
          });
        }
      });

    // Figure out if the map is read only. also check to see if there are any open cohorts
    // and if there aren't tell the user and send them back to the dashboard. If there is
    // an open cohort check to see if there are any new ones and if the values have changed
    this.store
      .select(selectReadOnlyMapInfo)
      .pipe(
        skipWhile(data => data.activeSite === null),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.readOnlyMap = this.siteHelper.readOnlyMapCheck(data);
        this.canSaveMap = this.siteHelper.canSaveMap(data);
        if (this.canSaveMap) {
          this.siteService.resetAvailableCohorts(this.siteId).subscribe(site => {
            this.resetPotentialRevenue(data.activeSite); // reset revenue AFTER available cohorts have been set
            // this.store.dispatch(setActiveSiteSuccess({ data: site }));
            if (!data.isAdmin) {
              this.store.dispatch(
                updateSiteStep({
                  id: this.activeSite.id,
                  step: 'Stand Selection',
                })
              );
              if (site.availableCohorts.length === 0) {
                this.showNoBuyersDialog();
              } else if (site.status === StatusEnum.REQUIRES_LANDOWNER_APPROVAL) {
                this.dialog.open(DialogYesNoComponent, {
                  panelClass: 'fc-yes-no-dialog',
                  data: {
                    title: 'Site Approval Required',
                    line1:
                      'Changes have been made to your site to promote adherence to program guidelines. Please review the changes and approve the site to continue and resubmit.',
                    line2:
                      'You can reject the changes to enable additional editing of your site. This will alter your submission and may be subject to additional approval.',
                    buttonTextYes: 'OK',
                    buttonTextNo: '',
                  },
                });
              }
            }
          });
        } else if (!this.readOnlyMap) {
          this.resetPotentialRevenue(data.activeSite);
        }
      });
    this.store
      .select(selectMapControlState)
      .pipe(takeUntil(this.destroy$))
      .subscribe(mapControls => {
        this.currentMapControls = mapControls;
      });
  }

  resetPotentialRevenue(activeSite: Site) {
    this.gisService.getFullPotentialValuation(this.siteId).subscribe({
      next: results => {
        if (results) {
          let siteClone = cloneDeep(activeSite);
          siteClone.simulatedFinancials = results;
          this.potentialRevenue = this.formatHelper.formatMinMaxValuation(siteClone.getAverageAnnualRevenue());
        } else {
          this.potentialRevenue = '$0.00';
        }
      },
      error: () => {
        this.toastService.sendMessage('Error getting potential site valuation.', ToastLevel.WARN);
      },
    });
  }

  ngAfterViewInit(): void {
    this.drawer.openedChange.pipe(takeUntil(this.destroy$)).subscribe(opened => {
      if (!opened) {
        this.map.resize();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.mapEvents$.complete();
    this.standListHover$.complete();
  }

  showNoBuyersDialog() {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'No Buyers Available',
          line1:
            "We're sorry, but there are no longer any buyers available for your selected parcel(s). You can create a new site from different parcels if you wish to now.",
          line2: '',
          buttonTextYes: 'OK',
          buttonTextNo: '',
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.redirectService.redirectToDashboard();
      });
  }

  haveChangesBeenMade(): boolean {
    return EditMapDeactivateModalService.getUnsavedChangesStatus();
  }

  onStandListHoverEvent(isHovered: boolean, standId: string): void {
    this.standListHover$.next({ isHovered, standId });
  }

  onStandListEvent(mapEvent: MapEvent): void {
    this.mapEvents$.next(mapEvent);
  }

  handleMapInitialize(data: any): void {
    this.map = data.map;
    this.originalBoundingBox = data.originalBoundingBox;
  }

  handleSaveSite(): void {
    if (this.isAdmin && this.activeSite.status !== StatusEnum.REQUIRES_LANDOWNER_APPROVAL) {
      this.dialog
        .open(DialogYesNoComponent, {
          panelClass: 'fc-yes-no-dialog',
          data: {
            title: 'Please Confirm',
            line1: `By saving these stands, you will cause the site to change to Requires Landowner Approval.`,
            line2: `You will have the option after saving to send an email to the landowner requesting approval.`,
            buttonTextYes: 'Confirm',
            buttonTextNo: 'Cancel',
          },
          autoFocus: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result === true) {
            this.doSiteSave();
          }
        });
    } else {
      this.doSiteSave();
    }
  }

  changeStyle(oldStyle, newStyle) {
    const updateStyle = newStyle;
    // ensure any sources from the current style are copied across to the new style
    updateStyle.sources = { ...oldStyle.sources, ...newStyle.sources };

    // find the index of where to insert our layers to retain in the new style
    let labelIndex = updateStyle.layers.findIndex(el => {
      return el.id === 'waterways-data1';
    });

    if (labelIndex === -1) {
      labelIndex = updateStyle.layers.length;

      const appLayers = oldStyle.layers.filter(el => {
        // app layers are the layers to retain, and these are any layers which have a different source set
        return (
          el.source && el.source !== 'mapbox://mapbox.satellite' && el.source !== 'mapbox' && el.source !== 'composite'
        );
      });

      updateStyle.layers = [
        ...updateStyle.layers.slice(0, labelIndex),
        ...appLayers,
        ...updateStyle.layers.slice(labelIndex, -1),
      ];
    }

    const parcelOutline = updateStyle.layers.findIndex(l => l.id === 'parcel-data-outline2');
    if (parcelOutline > -1) {
      updateStyle.layers[parcelOutline].paint['line-color'] =
        newStyle == this.screenshotDefinition || this.currentMapControls.displayContour == MapDisplayTypeEnum.CONTOUR
          ? 'black'
          : 'white';
    }

    this.map.setStyle(updateStyle, { diff: false });

    this.map.once('styledata', () => {
      if (!this.map.hasImage('blank-white')) {
        this.map.loadImage('../../../../../assets/blank-white.png', (err, image) => {
          if (err) throw err;
          this.map.addImage('blank-white', image);
        });
      }
      if (!this.map.hasImage('grid-fill')) {
        this.map.loadImage('../../../../../assets/grid-fill.png', (err, image) => {
          if (err) throw err;
          this.map.addImage('grid-fill', image);
        });
      }
    });
  }

  confirmSiteMapCreation() {
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Create New Map',
        line1: 'This will create a new map PNG based on the size of your window.',
        line2: 'Are you sure you want to continue?',
        buttonTextYes: 'Yes, Create',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.createSiteMap(false);
      }
    });
  }

  createSiteMap(redirect = true) {
    this.spinnerService.show('app-spinner', true, 'Creating Site Map');
    this.setMapInteractivity(false);
    let revertKML = false,
      revertDrawer = false,
      revertForest = false,
      revertWaterways = false,
      revertPublic = false;
    if (this.drawer.opened) {
      this.drawer.toggle();
      revertDrawer = true;
    }
    if (redirect) {
      this.map.fitBounds(this.originalBoundingBox, {
        padding: 100,
        animate: false,
      });
    }
    const originalStyle = this.map.getStyle();
    this.changeStyle(originalStyle, this.screenshotDefinition);

    this.map.once('styledata', () => {
      if (this.currentMapControls.displayForest) {
        revertForest = true;
        this.store.dispatch(toggleMapControlsForest());
      }
      if (this.currentMapControls.displayKMLUpload) {
        revertKML = true;
        this.store.dispatch(toggleMapControlsKMLUpload());
      }
      if (this.currentMapControls.displayWaterways) {
        revertWaterways = true;
        this.store.dispatch(toggleMapControlsWaterways());
      }
      if (!this.currentMapControls.displayStands) {
        this.store.dispatch(toggleMapControlsStands());
      }
      if (this.currentMapControls.displayPublicLand) {
        revertPublic = true;
        this.store.dispatch(toggleMapControlsPublicLand());
      }
      this.map.once('idle', () => {
        const mapCanvas = this.mapLayersComponent.mapElement.nativeElement;
        // hide everything except legend and scale
        mapCanvas.classList.add('map-screenshot');
        html2canvas(mapCanvas).then((canvas: any) => {
          // Get 2D context of the canvas
          const ctx = canvas.getContext('2d');

          // Capture the original image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // Convert the image to grayscale
          for (let i = 0; i < data.length; i += 4) {
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i] = avg; // Red channel
            data[i + 1] = avg; // Green channel
            data[i + 2] = avg; // Blue channel
          }

          // Put the modified image data back to the canvas
          ctx.putImageData(imageData, 0, 0);

          const arr = canvas.toDataURL('image/png').split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          const file = new File([u8arr], `${new Date().getTime()}_property_map.png`, { type: mime });

          const newDoc = {
            site_id: this.activeSite.id,
            document_type_id: this.documentService.getDocTypeId('Map'),
            name: file.name,
          };
          this.store.dispatch(
            createDocument({
              siteIdentifier: this.activeSite.identifier,
              document: newDoc,
              file,
            })
          );
          if (redirect) {
            this.router.navigateByUrl(`/site/${this.activeSite.id}/site-offer`);
            this.spinnerService.hide('app-spinner');
          } else {
            this.dialog.open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Map Created',
                line1: 'A new site map was created. You can view it in the documents area on the Site Details page.',
                buttonTextYes: 'Ok',
              },
              autoFocus: false,
            });
            if (revertKML) {
              this.store.dispatch(toggleMapControlsKMLUpload());
            }
            if (revertForest) {
              this.store.dispatch(toggleMapControlsForest());
            }
            if (revertWaterways) {
              this.store.dispatch(toggleMapControlsWaterways());
            }
            if (revertPublic) {
              this.store.dispatch(toggleMapControlsPublicLand());
            }
            if (revertDrawer) {
              this.drawer.toggle();
            }
            mapCanvas.classList.remove('map-screenshot');
            this.changeStyle(this.screenshotDefinition, originalStyle);

            this.spinnerService.hide('app-spinner');
            this.setMapInteractivity(true);
          }
        });
      });
    });
  }

  doSubmitSite(): void {
    if (this.activeSite.area < this.absMin) {
      this.dialog.open(SiteSizeDialogComponent, {
        panelClass: 'fc-dialog-site-size',
        data: { absMin: this.absMin },
      });
    } else if (this.activeSite.area > environment.site.maxSize || this.activeSite.area < environment.site.minSize) {
      this.dialog
        .open(SiteSizeDialogComponent, {
          panelClass: 'fc-dialog-site-size',
          data: {
            tooBig: this.activeSite.area > environment.site.maxSize,
          },
        })
        .afterClosed()
        .subscribe(result => {
          if (result === true) {
            this.createSiteMap();
          }
        });
    } else {
      this.createSiteMap();
    }
  }

  doSiteSave(): void {
    this.siteService.updateSiteStands(this.activeSite.id, this.selectedStandList).subscribe(
      site => {
        this.store.dispatch(updateNewImprovedSiteSuccess({ site, readyForSubmit: false }));
        //Add check here
        this.gisService.getOverlappingStands(this.activeSite.id).then(standIds => {
          if (standIds.length === 0) {
            this.store.dispatch(
              updateNewImprovedSiteSuccess({
                site,
                readyForSubmit: true,
              })
            );
          } else {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.panelClass = 'fc-yes-no-dialog';
            dialogConfig.data = { siteId: this.activeSite.id };
            dialogConfig.autoFocus = false;

            this.overlappingDialogRef = this.dialog.open(DialogOverlappingStandsComponent, dialogConfig);
            this.overlappingDialogRef.afterClosed().subscribe((result: boolean) => {
              if (result) {
                this.store.dispatch(setActiveSite({ id: this.activeSite.id }));
                this.store.dispatch(
                  updateNewImprovedSiteSuccess({
                    site,
                    readyForSubmit: true,
                  })
                );
              }
              this.overlappingDialogRef = null;
            });
          }
        });
      },
      error => this.store.dispatch(updateSiteFailure({ error }))
    );
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (EditMapDeactivateModalService.isLoggingOut) {
      EditMapDeactivateModalService.isLoggingOut = false;
      return true;
    } else if (EditMapDeactivateModalService.getUnsavedChangesStatus()) {
      this.editMapDeactivateModalService.open();
      return this.editMapDeactivateModalService.navigateAwaySelection$;
    } else {
      return true;
    }
  }

  selectAll(): void {
    const parcelIds = this.mapParcels.map(p => p.gid);
    const regionId = this.activeSite.cohort.region.id;
    const siteId = this.activeSite.id;
    if (this.selectedStandList.length > 0) {
      const dialogRef = this.dialog.open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Select Entire Property',
          line1: 'Selecting this option will remove all previously created stands. Are you sure you want to continue?',
          buttonTextYes: 'Yes, Continue',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          if (!this.currentMapControls.displayStands) {
            this.store.dispatch(toggleMapControlsStands());
          }
          this.store.dispatch(selectAllStands({ data: { parcelIds, regionId, siteId } }));
        }
      });
    } else {
      this.store.dispatch(selectAllStands({ data: { parcelIds, regionId, siteId } }));
      if (!this.currentMapControls.displayStands) {
        this.store.dispatch(toggleMapControlsStands());
      }
    }
  }

  deletePublicOverlap() {
    this.store.dispatch(setLastSaveIsValid({ isValid: false }));
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'REMOVE OVERLAP',
          line1:
            'Are you sure you want to remove all overlap with red flag layer? This will permanently change the site map and remove ' +
            this.publicOverlap +
            ' forested acres.',
          buttonTextYes: 'Yes, Remove',
          buttonTextNo: 'No, Cancel',
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((doDelete: boolean) => {
        if (doDelete) {
          this.spinnerService.show('app-spinner');
          this.gisService.removePublicOverlap(this.activeSite.id).subscribe(
            response => {
              this.spinnerService.hide('app-spinner');
              this.store.dispatch(removeRedFlagSuccess({ stands: response.results }));
            },
            error => {
              this.store.dispatch(removeRedFlagFailure({ error }));
              this.spinnerService.hide('app-spinner');
            }
          );
        }
      });
  }

  projectionInfoBox(year: string): string {
    if (this.activeSite?.cohort?.region) {
      return this.activeSite.cohort.region.projectionInfoText(year);
    }
    return '';
  }

  setMapInteractivity(enable: boolean) {
    if (enable) {
      this.map['scrollZoom'].enable();
      this.map['boxZoom'].enable();
      this.map['dragRotate'].enable();
      this.map['keyboard'].enable();
      this.map['dragPan'].enable();
      this.map['doubleClickZoom'].enable();
      this.map['touchZoomRotate'].enable();
    } else {
      this.map['scrollZoom'].disable();
      this.map['boxZoom'].disable();
      this.map['dragRotate'].disable();
      this.map['keyboard'].disable();
      this.map['dragPan'].disable();
      this.map['doubleClickZoom'].disable();
      this.map['touchZoomRotate'].disable();
    }
  }

  showHelpDialog() {
    window.open('/help', '_blank');
  }

  rejectAdminChanges() {
    this.isSelectAllDisabled = false;
    this.changeDetectorRef.detectChanges();
  }
}
